import { atomWithReset as stateSegmentWithReset } from 'jotai/utils';
import { CannedParams } from '../../../types/Content';
import {
  ContentRuleCategory,
  SeveritySummaryItem,
  CategoryRuleCount,
  ContentRule,
} from '../../../types/ContentRules';

/**
 * Options type for dropdown filters with loading state
 */
type OptionsType = {
  loading: boolean;
  options: CannedParams[];
  isFetched?: boolean;
  isSuccess?: boolean;
};

/**
 * Content Rule Center filter options state
 */
export type ContentRuleCenterFilterOptions = {
  countriesOptions: OptionsType;
  brandsOptions: OptionsType;
  audiencesOptions: OptionsType;
  channelsOptions: OptionsType;
  categoriesOptions: {
    loading: boolean;
    options: ContentRuleCategory[];
    isFetched?: boolean;
    isSuccess?: boolean;
  };
};

/**
 * Selected filter values for Content Rule Center
 */
export type ContentRuleCenterSelectedFilters = {
  countries: string[];
  brands: string[];
  audiences: string[];
  channels: string[];
  categories: string[];
};

/**
 * Severity summary state (quality cards)
 */
export type SeveritySummaryState = {
  loading: boolean;
  isFetched: boolean;
  totalRuleCount: string;
  severityRuleCounts: SeveritySummaryItem[];
  error: string | null;
};

/**
 * Category summary state (accordion data)
 */
export type CategorySummaryState = {
  loading: boolean;
  isFetched: boolean;
  categoryRuleCounts: CategoryRuleCount[];
  error: string | null;
};

/**
 * Content rules list state
 */
export type ContentRulesState = {
  loading: boolean;
  isFetched: boolean;
  rules: ContentRule[];
  pagination: {
    page: number;
    pageSize: number;
    totalPages: number;
    total: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  error: string | null;
};

/**
 * Per-category rules state for lazy loading
 */
export type CategoryRulesState = {
  [categoryId: string]: {
    loading: boolean;
    isFetched: boolean;
    rules: ContentRule[];
    pagination: {
      page: number;
      pageSize: number;
      totalPages: number;
      total: number;
      nextPage: number | null;
      previousPage: number | null;
    };
    error: string | null;
  };
};

/**
 * Complete Content Rule Center state
 */
export type ContentRuleCenterStateTypes = {
  // Filter options (dropdown data)
  filterOptions: ContentRuleCenterFilterOptions;

  // Selected filter values
  selectedFilters: ContentRuleCenterSelectedFilters;

  // Severity summary (quality cards data)
  severitySummary: SeveritySummaryState;

  // Category summary (accordion data)
  categorySummary: CategorySummaryState;

  // Content rules list (deprecated - use categoryRules instead)
  contentRules: ContentRulesState;

  // Per-category rules for lazy loading
  categoryRules: CategoryRulesState;

  // Overall loading state for initial fetch (quality cards + dropdowns)
  isInitialLoading: boolean;

  // Loading state for table content (category summaries)
  isTableLoading: boolean;

  // Flag to track if initial data (quality cards + dropdowns) has been fetched
  isInitialized: boolean;

  // Flag to track if filter options are loaded
  areFiltersLoaded: boolean;

  // Overall error message
  error: string | null;
};

/**
 * Default initial state for Content Rule Center
 */
const defaultFilterOptions: ContentRuleCenterFilterOptions = {
  countriesOptions: { loading: false, options: [], isFetched: false },
  brandsOptions: { loading: false, options: [], isFetched: false },
  audiencesOptions: { loading: false, options: [], isFetched: false },
  channelsOptions: { loading: false, options: [], isFetched: false },
  categoriesOptions: { loading: false, options: [], isFetched: false },
};

const defaultSelectedFilters: ContentRuleCenterSelectedFilters = {
  countries: [],
  brands: [],
  audiences: [],
  channels: [],
  categories: [],
};

const defaultSeveritySummary: SeveritySummaryState = {
  loading: false,
  isFetched: false,
  totalRuleCount: '0',
  severityRuleCounts: [],
  error: null,
};

const defaultCategorySummary: CategorySummaryState = {
  loading: false,
  isFetched: false,
  categoryRuleCounts: [],
  error: null,
};

const defaultContentRules: ContentRulesState = {
  loading: false,
  isFetched: false,
  rules: [],
  pagination: {
    page: 1,
    pageSize: 20,
    totalPages: 0,
    total: 0,
    nextPage: null,
    previousPage: null,
  },
  error: null,
};

/**
 * Content Rule Center state segment (Jotai atom)
 * Manages all state for the Content Rule Center modal
 */
export const contentRuleCenterStateSegment =
  stateSegmentWithReset<ContentRuleCenterStateTypes>({
    filterOptions: defaultFilterOptions,
    selectedFilters: defaultSelectedFilters,
    severitySummary: defaultSeveritySummary,
    categorySummary: defaultCategorySummary,
    contentRules: defaultContentRules,
    categoryRules: {},
    isInitialLoading: false,
    isTableLoading: false,
    isInitialized: false,
    areFiltersLoaded: false,
    error: null,
  });

contentRuleCenterStateSegment.debugLabel = 'contentRuleCenterStateSegment';
